import { useCallback, useEffect, useState } from "react";

import {
  Alert,
  Button,
  Divider,
  InputAdornment,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";

import { apiClient, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomNumberField,
  ProductVariantPicker,
  useProductVariantPicker,
} from "@/features/ui";

import {
  StockAdjustmentTypeEnum,
  StockAdjustmentTypeLabel,
} from "../stock-adjustment-form-constants";

const InputModeEnum = Object.freeze({
  ADDITION: "ADDITION",
  DEDUCTION: "DEDUCTION",
  SET_STOCK: "SET_STOCK",
});

const INITIAL_STATE = {
  mode: InputModeEnum.ADDITION,
  quantity: 1,
  newStock: 0,
  costPerUnit: 0,
};

export const AddStockAdjustmentProductVariantSection = ({
  onAdded,
  warehouse,
  outletId,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const picker = useProductVariantPicker();
  const { product, productVariant, loading, reset } = picker;

  const [mode, setMode] = useState(INITIAL_STATE.mode);
  const [quantity, setQuantity] = useState(INITIAL_STATE.quantity);
  const [newStock, setNewStock] = useState(INITIAL_STATE.newStock);
  const [costPerUnit, setCostPerUnit] = useState(INITIAL_STATE.costPerUnit);

  const [currentStock, setCurrentStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockError, setStockError] = useState(null);

  const resetLocalState = useCallback(() => {
    setMode(INITIAL_STATE.mode);
    setQuantity(INITIAL_STATE.quantity);
    setNewStock(INITIAL_STATE.newStock);
    setCostPerUnit(INITIAL_STATE.costPerUnit);
    setCurrentStock(null);
    setStockError(null);
  }, []);

  const fetchCurrentStock = useCallback(async (sku, warehouseId) => {
    setCurrentStock(null);
    setStockError(null);
    setLoadingStock(true);
    try {
      const {
        data: { data },
      } = await apiClient.get("warehouse-stocks/balance", {
        params: { warehouseId, sku },
      });
      setCurrentStock(data?.quantity ?? 0);
    } catch (error) {
      setStockError(getErrorMessage(error, "Gagal memuat stok saat ini"));
      setCurrentStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== InputModeEnum.SET_STOCK) return;
    if (!productVariant || !warehouse) return;
    fetchCurrentStock(productVariant.sku, warehouse.id);
  }, [mode, productVariant, warehouse, fetchCurrentStock]);

  const computedDelta =
    mode === InputModeEnum.SET_STOCK && currentStock !== null
      ? Number(newStock) - currentStock
      : null;

  const isCostVisible =
    mode === InputModeEnum.ADDITION ||
    (mode === InputModeEnum.SET_STOCK &&
      computedDelta !== null &&
      computedDelta > 0);

  const handleModeChange = (_, newMode) => {
    if (newMode === null) return;
    setMode(newMode);
    setQuantity(INITIAL_STATE.quantity);
    setNewStock(INITIAL_STATE.newStock);
    setCostPerUnit(INITIAL_STATE.costPerUnit);
    setCurrentStock(null);
    setStockError(null);
  };

  const canAdd = (() => {
    if (!productVariant) return false;
    if (productVariant?.status === false) return false;
    if (!warehouse) return false;

    if (mode === InputModeEnum.SET_STOCK) {
      if (currentStock === null) return false;
      if (computedDelta === 0) return false;
      if (computedDelta > 0 && Number(costPerUnit) < 0) return false;
      return true;
    }

    if (Number(quantity) <= 0) return false;
    if (mode === InputModeEnum.ADDITION && Number(costPerUnit) < 0)
      return false;
    return true;
  })();

  const handleAdd = () => {
    let line;
    if (mode === InputModeEnum.SET_STOCK) {
      const delta = computedDelta;
      if (delta > 0) {
        line = {
          type: StockAdjustmentTypeEnum.ADDITION,
          quantity: delta,
          costPerUnit: Number(costPerUnit),
        };
      } else {
        line = {
          type: StockAdjustmentTypeEnum.DEDUCTION,
          quantity: Math.abs(delta),
          costPerUnit: 0,
        };
      }
    } else if (mode === InputModeEnum.ADDITION) {
      line = {
        type: StockAdjustmentTypeEnum.ADDITION,
        quantity: Number(quantity),
        costPerUnit: Number(costPerUnit),
      };
    } else {
      line = {
        type: StockAdjustmentTypeEnum.DEDUCTION,
        quantity: Number(quantity),
        costPerUnit: 0,
      };
    }

    onAdded(productVariant, product, line);
    reset();
    resetLocalState();
  };

  const isSetStock = mode === InputModeEnum.SET_STOCK;

  return (
    <Stack spacing={2} direction={isSmallScreen ? "column" : "row"}>
      <Grid
        container
        spacing={2}
        sx={{
          flex: 2,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <ProductVariantPicker picker={picker} outletId={outletId} />
        </Grid>
      </Grid>
      <Divider
        sx={{ py: 0 }}
        orientation={isSmallScreen ? "horizontal" : "vertical"}
        flexItem
      />
      <Grid
        container
        spacing={2}
        sx={{
          flex: 1.4,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <Typography variant="body2">SKU Produk Varian</Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              mt: 1,
            }}
          >
            {loading.variant ? "Loading ..." : (productVariant?.sku ?? "-")}
            {productVariant && productVariant?.status === false && (
              <Typography
                component="span"
                variant="body1"
                color="error"
                sx={{ ml: 1 }}
              >
                (Tidak aktif)
              </Typography>
            )}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
            }}
          >
            Tipe Penyesuaian
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={handleModeChange}
            disabled={!productVariant}
          >
            <ToggleButton value={InputModeEnum.ADDITION}>
              {StockAdjustmentTypeLabel[StockAdjustmentTypeEnum.ADDITION]}
            </ToggleButton>
            <ToggleButton value={InputModeEnum.DEDUCTION}>
              {StockAdjustmentTypeLabel[StockAdjustmentTypeEnum.DEDUCTION]}
            </ToggleButton>
            <ToggleButton value={InputModeEnum.SET_STOCK}>
              Atur Stok
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {!warehouse && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              Pilih gudang pada Informasi Umum terlebih dahulu untuk menambah
              produk.
            </Alert>
          </Grid>
        )}

        {isSetStock && warehouse && (
          <>
            <Grid size={6}>
              <CustomNumberField
                value={
                  loadingStock || currentStock === null ? "" : currentStock
                }
                disabled
                label="Stok Saat Ini"
                inputProps={{ allowNegative: true }}
                InputProps={{
                  placeholder: loadingStock ? "Loading..." : "-",
                }}
              />
            </Grid>
            <Grid size={6}>
              <CustomNumberField
                value={newStock}
                onChange={(e) => setNewStock(e.target.value)}
                disabled={!productVariant || currentStock === null}
                label="Stok Baru"
                required
                InputProps={{ placeholder: "Cth: 50" }}
                inputProps={{ min: 0 }}
                helperText={
                  computedDelta !== null
                    ? computedDelta === 0
                      ? "Tidak ada perubahan stok"
                      : `Selisih: ${computedDelta > 0 ? "+" : ""}${formatNumber(computedDelta)} (akan dicatat sebagai ${
                          computedDelta > 0
                            ? StockAdjustmentTypeLabel[
                                StockAdjustmentTypeEnum.ADDITION
                              ]
                            : StockAdjustmentTypeLabel[
                                StockAdjustmentTypeEnum.DEDUCTION
                              ]
                        })`
                    : undefined
                }
                FormHelperTextProps={{
                  sx: {
                    color:
                      computedDelta === null
                        ? "text.secondary"
                        : computedDelta === 0
                          ? "error.main"
                          : computedDelta > 0
                            ? "success.main"
                            : "warning.main",
                  },
                }}
              />
            </Grid>
            {stockError && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="error">
                  {stockError}
                </Typography>
              </Grid>
            )}
          </>
        )}

        {!isSetStock && (
          <Grid size={isCostVisible ? 6 : 12}>
            <CustomNumberField
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={!productVariant}
              label="Jumlah"
              required
              InputProps={{ placeholder: "Cth: 5" }}
              inputProps={{ min: 1 }}
            />
          </Grid>
        )}

        {isCostVisible && (
          <Grid size={6}>
            <CustomNumberField
              value={costPerUnit}
              onChange={(e) => setCostPerUnit(e.target.value)}
              disabled={!productVariant}
              label="Biaya Satuan (Rp)"
              required
              InputProps={{
                placeholder: "Cth: 10000",
                startAdornment: (
                  <InputAdornment position="start">Rp</InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={!canAdd}
            onClick={handleAdd}
          >
            Tambah
          </Button>
        </Grid>
      </Grid>
    </Stack>
  );
};

AddStockAdjustmentProductVariantSection.propTypes = {
  onAdded: PropTypes.func.isRequired,
  warehouse: PropTypes.object,
  outletId: PropTypes.string,
};
